import React from "react";
import { Link, useParams } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useSEO } from "@/hooks/useSEO";
import { stateRouteMeta, canonicalUrl } from "@/seo/routeMeta";
import { getStateBySlug, states } from "@/data/stateData";
import NotFound from "./NotFound";

const StatePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const state = slug ? getStateBySlug(slug) : undefined;

  const meta = state ? stateRouteMeta(state) : undefined;

  useSEO({
    title: meta?.title ?? "State Dating Pool Statistics",
    description: meta?.description ?? "",
    keywords: meta?.keywords,
    canonical: state ? canonicalUrl(`/states/${state.slug}`) : undefined,
    structuredData: meta?.structuredData,
    noindex: meta?.noindex,
  });

  if (!state) {
    return <NotFound />;
  }

  const shareOfUs = `${(state.shareOfUsWomen * 100).toFixed(1)}%`;
  const index = states.findIndex((s) => s.slug === state.slug);
  const prev = states[index - 1];
  const next = states[index + 1];

  const statCards = [
    {
      value: state.women18to44.toLocaleString(),
      label: "estimated women aged 18-44",
    },
    {
      value: shareOfUs,
      label: "of all US women aged 18-44",
    },
    {
      value: state.medianAge.toFixed(1),
      label: "state median age",
    },
    {
      value: `${Math.round(state.pctBachelorsWomen * 100)}%`,
      label: "of women hold a bachelor's degree or higher",
    },
    {
      value: `${Math.round(state.pctNeverMarriedWomen * 100)}%`,
      label: "of women have never been married",
    },
    {
      value: state.largestMetro,
      label: "largest metro area",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <Breadcrumbs />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-10">
          <header className="text-center space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Dating Pool in {state.name}
            </h1>
            <p className="text-xl text-muted-foreground">
              Demographic estimates for women aged 18-44 in {state.name} ({state.abbreviation}),
              based on US Census Bureau data.
            </p>
          </header>

          <main className="space-y-10">
            <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {statCards.map((stat, i) => (
                <Card key={i}>
                  <CardContent className="pt-6 text-center">
                    <div className="text-2xl font-extrabold text-primary break-words">
                      {stat.value}
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
                  </CardContent>
                </Card>
              ))}
            </section>

            <section className="space-y-4">
              <h2 className="text-2xl font-semibold">
                What the numbers mean for dating in {state.name}
              </h2>
              {state.description.map((paragraph, i) => (
                <p key={i} className="leading-relaxed text-muted-foreground">
                  {paragraph}
                </p>
              ))}
              <p className="leading-relaxed text-muted-foreground">
                A national probability from our calculator scales roughly with a state's
                share of the US population: preferences matching 1% of US women translate
                to about{" "}
                <strong className="text-foreground">
                  {Math.round(state.women18to44 * 0.01).toLocaleString()} women
                </strong>{" "}
                in {state.name}. State demographics like education and marriage rates
                shift the real figure up or down.
              </p>
            </section>

            <section className="bg-muted/50 rounded-lg p-6 text-center space-y-3">
              <h2 className="text-xl font-semibold">
                Calculate your match probability
              </h2>
              <p className="text-muted-foreground">
                Combine age, education, body type, and more filters against national data,
                then scale to {state.name}'s {state.women18to44.toLocaleString()} women aged 18-44.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/" className="text-primary hover:underline font-medium">
                  Female Partner Calculator →
                </Link>
                <Link to="/ideal-woman-calculator" className="text-primary hover:underline font-medium">
                  Ideal Woman Calculator →
                </Link>
                <Link to="/male-partner-calculator" className="text-primary hover:underline font-medium">
                  Male Partner Calculator →
                </Link>
                <Link to="/dating-standards-calculator" className="text-primary hover:underline font-medium">
                  Dating Standards Test →
                </Link>
              </div>
            </section>

            <section className="space-y-3">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Methodology note</CardTitle>
                  <CardDescription>
                    Estimates combine 2023 Census Bureau state population figures with
                    American Community Survey age, sex, education, and marital status
                    distributions. Figures are rounded estimates for statistical
                    education, not precise counts. See our{" "}
                    <Link to="/methodology" className="text-primary hover:underline">
                      methodology
                    </Link>{" "}
                    and{" "}
                    <Link to="/data-sources" className="text-primary hover:underline">
                      data sources
                    </Link>
                    .
                  </CardDescription>
                </CardHeader>
              </Card>
            </section>

            <nav className="flex justify-between border-t pt-6 text-sm" aria-label="State navigation">
              <span>
                {prev && (
                  <Link to={`/states/${prev.slug}`} className="text-primary hover:underline">
                    ← {prev.name}
                  </Link>
                )}
              </span>
              <Link to="/states" className="text-muted-foreground hover:text-primary">
                All states
              </Link>
              <span>
                {next && (
                  <Link to={`/states/${next.slug}`} className="text-primary hover:underline">
                    {next.name} →
                  </Link>
                )}
              </span>
            </nav>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default StatePage;
